import { motion } from 'framer-motion';
import {
  BarChart3,
  Briefcase,
  Building2,
  Filter,
  Package,
  Sparkles,
  User,
  UserCheck,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { CustomSelect } from './CustomSelect';

interface EhrChurnByPhaseChartProps {
  data: {
    phases: Record<string, number>;
    records: Array<{
      Id: string;
      Churned__c: boolean;
      Product_Downgrade__c: boolean;
      Owner: { Name: string };
      Onboarding_Rep__r?: { Name: string };
      Official_Change_Date__c: string;
      CreatedDate?: string;
      Start_Date__c?: string;
      Go_Live_Date__c?: string;
    }>;
  };
  title?: string;
  isLoading?: boolean;
}

const EhrChurnByPhaseChart = ({
  data,
  title = 'EHR Churn by Phase (All Time)',
  isLoading = false,
}: EhrChurnByPhaseChartProps) => {
  const [hoveredPhase, setHoveredPhase] = useState<string | null>(null);
  const [churnTypeFilter, setChurnTypeFilter] = useState<string>('all');
  const [onboardingCoachFilter, setOnboardingCoachFilter] = useState<string>('all');
  const [salesRepFilter, setSalesRepFilter] = useState<string>('all');

  // Extract unique onboarding coaches and sales reps from records
  const { onboardingCoaches, salesReps } = useMemo(() => {
    const coaches = new Set<string>();
    const reps = new Set<string>();

    data.records.forEach(record => {
      if (record.Onboarding_Rep__r?.Name) {
        coaches.add(record.Onboarding_Rep__r.Name);
      }

      if (record.Owner?.Name) {
        reps.add(record.Owner.Name);
      }
    });

    return {
      onboardingCoaches: Array.from(coaches).sort(),
      salesReps: Array.from(reps).sort(),
    };
  }, [data.records]);

  // Filter records and recalculate phases based on selected filters
  const filteredData = useMemo(() => {
    const filtered = data.records.filter(record => {
      // Churn type filter
      if (churnTypeFilter === 'logo' && !record.Churned__c) return false;
      if (churnTypeFilter === 'product' && !record.Product_Downgrade__c) return false;

      // Onboarding coach filter
      if (
        onboardingCoachFilter !== 'all' &&
        record.Onboarding_Rep__r?.Name !== onboardingCoachFilter
      ) {
        return false;
      }

      // Sales rep filter
      if (salesRepFilter !== 'all' && record.Owner?.Name !== salesRepFilter) {
        return false;
      }

      return true;
    });

    // Recalculate phases for filtered records
    const phases = {
      'Phase 0': 0,
      'Phase 1': 0,
      'Phase 2': 0,
      'Phase 3': 0,
      'Phase 4': 0,
      'Phase 5': 0,
      'Phase 6': 0,
      'Phase 7': 0,
    };

    filtered.forEach(record => {
      const changeDate = new Date(record.Official_Change_Date__c);
      const createdDate = record.CreatedDate ? new Date(record.CreatedDate) : null;
      const startDate = record.Start_Date__c ? new Date(record.Start_Date__c) : null;
      const goLiveDate = record.Go_Live_Date__c ? new Date(record.Go_Live_Date__c) : null;

      // Phase 0: Churned between contract creation and start (if start date exists)
      if (createdDate && startDate && changeDate >= createdDate && changeDate < startDate) {
        phases['Phase 0']++;
      }
      // Phase 1: Churned between start and go-live (if both exist)
      else if (startDate && goLiveDate && changeDate >= startDate && changeDate < goLiveDate) {
        phases['Phase 1']++;
      }
      // If no start date but has created and go-live, phase 0/1 is between created and go-live
      else if (
        !startDate &&
        createdDate &&
        goLiveDate &&
        changeDate >= createdDate &&
        changeDate < goLiveDate
      ) {
        phases['Phase 0']++;
      }
      // Phases 2-7: After go-live
      else if (goLiveDate && changeDate >= goLiveDate) {
        const monthsDiff =
          (changeDate.getFullYear() - goLiveDate.getFullYear()) * 12 +
          (changeDate.getMonth() - goLiveDate.getMonth());

        if (monthsDiff <= 1) phases['Phase 2']++;
        else if (monthsDiff <= 3) phases['Phase 3']++;
        else if (monthsDiff <= 6) phases['Phase 4']++;
        else if (monthsDiff <= 9) phases['Phase 5']++;
        else if (monthsDiff <= 12) phases['Phase 6']++;
        else phases['Phase 7']++;
      }
      // Default: If go-live date is missing, can't categorize properly
      else {
        phases['Phase 7']++;
      }
    });

    return { phases, totalChurned: filtered.length };
  }, [data.records, churnTypeFilter, onboardingCoachFilter, salesRepFilter]);

  const phases = [
    'Phase 0',
    'Phase 1',
    'Phase 2',
    'Phase 3',
    'Phase 4',
    'Phase 5',
    'Phase 6',
    'Phase 7',
  ] as const;

  type PhaseKey = (typeof phases)[number];

  const phaseDescriptions: Record<PhaseKey, string> = {
    'Phase 0': 'Contract to Start',
    'Phase 1': 'Start to Go-Live',
    'Phase 2': 'Within 1 Month',
    'Phase 3': '2-3 Months',
    'Phase 4': '3-6 Months',
    'Phase 5': '6-9 Months',
    'Phase 6': '9-12 Months',
    'Phase 7': '12+ Months',
  };

  const maxValue = Math.max(...Object.values(filteredData.phases), 1);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading chart data...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-200 dark:border-gray-700 overflow-visible"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
          <BarChart3 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Total Churned: {filteredData.totalChurned} opportunities
          </p>
        </div>
      </div>

      {/* Enhanced Filters */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-8 p-6 bg-gradient-to-br from-purple-50/50 via-white to-indigo-50/50 dark:from-gray-900/80 dark:via-gray-800/80 dark:to-purple-900/20 rounded-2xl border border-purple-200/50 dark:border-purple-800/30 shadow-lg"
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <Filter className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h4 className="text-base font-bold text-gray-900 dark:text-white">Filter Options</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Customize your view by churn type, coach, or sales rep
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Enhanced Churn Type Filter */}
          <div className="group">
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <BarChart3 className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
              Churn Type
              {churnTypeFilter !== 'all' && (
                <span className="ml-auto px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs rounded-full">
                  Active
                </span>
              )}
            </label>
            <CustomSelect
              value={churnTypeFilter}
              onChange={setChurnTypeFilter}
              options={[
                { value: 'all', label: 'All Types', icon: <Sparkles className="h-4 w-4" /> },
                { value: 'logo', label: 'Logo Churn', icon: <Building2 className="h-4 w-4" /> },
                { value: 'product', label: 'Product Churn', icon: <Package className="h-4 w-4" /> },
              ]}
              icon={<Sparkles className="h-4 w-4" />}
              activeColor="purple"
            />
          </div>

          {/* Enhanced Onboarding Coach Filter */}
          <div className="group">
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <UserCheck className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
              Onboarding Coach
              {onboardingCoachFilter !== 'all' && (
                <span className="ml-auto px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs rounded-full">
                  Active
                </span>
              )}
            </label>
            <CustomSelect
              value={onboardingCoachFilter}
              onChange={setOnboardingCoachFilter}
              options={[
                {
                  value: 'all',
                  label: `All Coaches (${onboardingCoaches.length})`,
                  icon: <Users className="h-4 w-4" />,
                },
                ...onboardingCoaches.map(coach => ({
                  value: coach,
                  label: coach,
                  icon: <User className="h-4 w-4" />,
                })),
              ]}
              icon={<Users className="h-4 w-4" />}
              activeColor="indigo"
            />
          </div>

          {/* Enhanced Sales Rep Filter */}
          <div className="group">
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <Users className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
              Sales Rep
              {salesRepFilter !== 'all' && (
                <span className="ml-auto px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs rounded-full">
                  Active
                </span>
              )}
            </label>
            <CustomSelect
              value={salesRepFilter}
              onChange={setSalesRepFilter}
              options={[
                {
                  value: 'all',
                  label: `All Sales Reps (${salesReps.length})`,
                  icon: <Briefcase className="h-4 w-4" />,
                },
                ...salesReps.map(rep => ({
                  value: rep,
                  label: rep,
                  icon: <User className="h-4 w-4" />,
                })),
              ]}
              icon={<Briefcase className="h-4 w-4" />}
              activeColor="blue"
            />
          </div>
        </div>

        {/* Active Filters Summary */}
        {(churnTypeFilter !== 'all' ||
          onboardingCoachFilter !== 'all' ||
          salesRepFilter !== 'all') && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-4 pt-4 border-t border-purple-200 dark:border-purple-800/30"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                Active Filters:
              </span>
              {churnTypeFilter !== 'all' && (
                <span className="px-3 py-1.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs font-medium rounded-full flex items-center gap-1.5">
                  {churnTypeFilter === 'logo' ? (
                    <>
                      <Building2 className="h-3.5 w-3.5" />
                      <span>Logo Churn</span>
                    </>
                  ) : (
                    <>
                      <Package className="h-3.5 w-3.5" />
                      <span>Product Churn</span>
                    </>
                  )}
                </span>
              )}
              {onboardingCoachFilter !== 'all' && (
                <span className="px-3 py-1.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs font-medium rounded-full flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  <span>{onboardingCoachFilter}</span>
                </span>
              )}
              {salesRepFilter !== 'all' && (
                <span className="px-3 py-1.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full flex items-center gap-1.5">
                  <Briefcase className="h-3.5 w-3.5" />
                  <span>{salesRepFilter}</span>
                </span>
              )}
              <button
                onClick={() => {
                  setChurnTypeFilter('all');
                  setOnboardingCoachFilter('all');
                  setSalesRepFilter('all');
                }}
                className="ml-auto px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Clear All
              </button>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Chart */}
      <div className="flex items-end justify-between gap-2 h-96 px-4 pt-20">
        {phases.map((phase, index) => {
          const value = (filteredData.phases as Record<string, number>)[phase] || 0;
          const percentage =
            filteredData.totalChurned > 0 ? (value / filteredData.totalChurned) * 100 : 0;
          const barHeight = maxValue > 0 ? (value / maxValue) * 100 : 0;

          return (
            <motion.div
              key={phase}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              className="flex-1 flex flex-col items-center group relative"
              onMouseEnter={() => setHoveredPhase(phase)}
              onMouseLeave={() => setHoveredPhase(null)}
            >
              {/* Tooltip */}
              {hoveredPhase === phase && value > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute -top-20 left-1/2 transform -translate-x-1/2 z-50 pointer-events-none"
                >
                  <div className="bg-gray-900 dark:bg-gray-700 text-white px-4 py-3 rounded-lg shadow-xl border border-gray-700 dark:border-gray-600 min-w-[200px]">
                    <div className="text-xs font-semibold text-gray-300 mb-1">{phase}</div>
                    <div className="text-sm text-gray-400 mb-2">{phaseDescriptions[phase]}</div>
                    <div className="text-lg font-bold">{value} opportunities</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {percentage.toFixed(1)}% of total churn
                    </div>
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900 dark:bg-gray-700 border-r border-b border-gray-700 dark:border-gray-600"></div>
                  </div>
                </motion.div>
              )}

              {/* Bar Container */}
              <div className="w-full flex flex-col justify-end" style={{ height: '300px' }}>
                {/* Value Display Above Bar */}
                {value > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 + 0.05 * index }}
                    className="text-center mb-2"
                  >
                    <div className="text-sm font-bold text-gray-900 dark:text-white">{value}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {percentage.toFixed(1)}%
                    </div>
                  </motion.div>
                )}

                {/* Bar */}
                <div
                  className="relative w-full bg-gray-100 dark:bg-gray-700 rounded-t-lg overflow-visible cursor-pointer"
                  style={{ height: '100%' }}
                >
                  <motion.div
                    className={`w-full rounded-t-lg transition-all duration-300 absolute bottom-0 ${
                      index <= 1
                        ? 'bg-gradient-to-t from-red-500 to-red-600' // Phase 0-1: Pre-go-live (high risk)
                        : percentage > 15
                          ? 'bg-gradient-to-t from-orange-500 to-orange-600' // High churn
                          : 'bg-gradient-to-t from-purple-500 to-purple-600' // Normal
                    }`}
                    initial={{ height: 0 }}
                    animate={{
                      height: value > 0 ? `${barHeight}%` : '0%',
                      scale: hoveredPhase === phase ? 1.05 : 1,
                    }}
                    transition={{ delay: 0.2 + 0.05 * index, duration: 0.8, ease: 'easeOut' }}
                    style={{
                      boxShadow:
                        hoveredPhase === phase ? '0 10px 25px -5px rgba(0, 0, 0, 0.3)' : 'none',
                    }}
                  />
                </div>
              </div>

              {/* Enhanced Label */}
              <div className="mt-3 flex flex-col items-center">
                <div
                  className={`text-xs font-semibold transition-all duration-200 ${
                    hoveredPhase === phase
                      ? 'text-gray-900 dark:text-white scale-110'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {phase}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                  {phaseDescriptions[phase]}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-4 justify-center text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-gradient-to-t from-red-500 to-red-600"></div>
            <span className="text-gray-600 dark:text-gray-400">Pre-Go-Live (Phase 0-1)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-gradient-to-t from-orange-500 to-orange-600"></div>
            <span className="text-gray-600 dark:text-gray-400">High Churn (&gt;15%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-gradient-to-t from-purple-500 to-purple-600"></div>
            <span className="text-gray-600 dark:text-gray-400">Normal Churn</span>
          </div>
        </div>
      </div>

      {/* Insights */}
      {filteredData.totalChurned > 0 && (
        <div className="mt-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
          <p className="text-sm text-purple-900 dark:text-purple-100">
            <strong>Insight:</strong> Most customers churned at{' '}
            {phases.reduce((max, phase) => {
              const phasesRecord = filteredData.phases as Record<string, number>;

              return (phasesRecord[phase] || 0) > (phasesRecord[max] || 0) ? phase : max;
            })}{' '}
            (
            {
              phaseDescriptions[
                phases.reduce((max, phase) => {
                  const phasesRecord = filteredData.phases as Record<string, number>;

                  return (phasesRecord[phase] || 0) > (phasesRecord[max] || 0) ? phase : max;
                }) as PhaseKey
              ]
            }
            )
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default EhrChurnByPhaseChart;
